import React from 'react';
import { GoogleAddressField } from '@pv/common/components';
import type { AddressType as Address } from '@pv/common/components';
import type { AddressInput } from '@pv/common/graphql/types';

export const emptyBillingAddress: Address = {
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  formattedAddress: '',
};

type NullableAddress = {
  [K in keyof Address]?: Address[K] | null;
};

export const toBillingAddress = (
  address?: NullableAddress | null
): Address => ({
  streetAddress1: address?.streetAddress1 || '',
  streetAddress2: address?.streetAddress2 || '',
  city: address?.city || '',
  state: address?.state || '',
  zip: address?.zip || '',
  country: address?.country || '',
  formattedAddress: address?.formattedAddress || '',
});

// Stripe Tax needs at least a country and postal code to place the customer;
// require the street and city too so invoices carry a real address.
export const isCompleteBillingAddress = (address?: Address | null) =>
  !!address &&
  [address.streetAddress1, address.city, address.zip, address.country].every(
    (part) => !!part?.trim()
  );

export const toBillingAddressInput = (address: Address): AddressInput => ({
  streetAddress1: address.streetAddress1,
  streetAddress2: address.streetAddress2 || '',
  city: address.city,
  state: address.state,
  zip: address.zip,
  country: address.country,
  formattedAddress: address.formattedAddress,
});

export const BillingAddressField = ({
  value,
  onChange,
  forceValidate = false,
  disabled = false,
}: {
  value: Address;
  onChange: (address: Address) => void;
  forceValidate?: boolean;
  disabled?: boolean;
}) => {
  const showError = forceValidate && !isCompleteBillingAddress(value);

  return (
    <GoogleAddressField
      label="Billing Address"
      value={value}
      onChange={onChange}
      disabled={disabled}
      required
      error={showError}
      helperText={
        showError
          ? 'Enter a full billing address, including postal code and country.'
          : ''
      }
    />
  );
};
